import {Inject, Injectable, InjectionToken, Optional} from '@angular/core';
import {BehaviorSubject, merge, NEVER, Observable, of, pipe, Subject, timer} from 'rxjs';
import {
    delay,
    distinctUntilChanged,
    filter,
    map,
    mapTo,
    share,
    switchMap,
    switchMapTo,
    take,
    tap
} from 'rxjs/operators';
import {dependOn} from '../../common/rxjs/depend-on';
import {AppConfigService} from '../../services/config.service';
import {GenericSystemStatusLogger, SYSTEM_STATUS_LOGGER_TOKEN} from '../system-status-indicator/system-status-logger';
import {StreamEventType, StreamNotReadyEvent, StreamReadyEvent} from './stream-event';
import {GenericStreamingService, WebSocketCommand, WebSocketMessage} from './streaming-interfaces';

export const STREAMING_SERVICE_CONFIG_TOKEN =
                 new InjectionToken<Partial<StreamingServiceConfig>>('streamingServiceConfig');

const enum SystemStatusLogCode {
    OPEN,
    CLOSE,
    ERROR,
    NOT_NEEDED
}

export interface StreamingServiceConfig {
    noPulseMessage: string;
    reconnectDelayStep: number;
    maxReconnectDelay: number;
    heartbeatInterval: number;
    socketUrl: string;
    systemStatusLogKeyCode: string;
}

export enum WebSocketConnectionState {
    OPEN    = 'OPEN',
    CLOSED  = 'CLOSED',
    OPENING = 'OPENING',
    CLOSING = 'CLOSING'
}

/**
 * RFC6455: Normal closure;
 * the connection successfully completed whatever purpose for which it was created.
 */
const NORMAL_DISCONNECT_CODE  = 1000;

/**
 * Connection closure code indicates that server doesn't respond for a long time.
 */
const NO_PULSE_DISCONNECT_CODE = 3010;

const SECOND = 1000;

/**
 * Timeout before socket will be disconnected when all subscribers are left.
 * It's window of time for the next subscribers come.
 * This period shouldn't be long in order to break the connection after logout as soon as possible.
 */
const LAZY_DISCONNECT_TIMEOUT = 300;

const HEARTBEAT_SOCKET_MESSAGE = 'hb';

@Injectable()
export class StreamingService implements GenericStreamingService {

    public config: StreamingServiceConfig = {
        noPulseMessage: 'Server doesn\'t respond for a long time.',
        reconnectDelayStep: 3 * SECOND,
        maxReconnectDelay: 60 * SECOND,
        heartbeatInterval: 25 * SECOND, // Default SockJS heartbeat interval
        socketUrl: this.appConfig.api['defaultStreaming'],
        systemStatusLogKeyCode: 'STR'
    };

    public message$: Observable<WebSocketMessage>;
    public ready$: Observable<StreamReadyEvent>;
    public notReady$: Observable<StreamNotReadyEvent>;

    /**
     * The same as {@link ready$} and {@link notReady$}, but don't keep connection.
     */
    public passiveReady$: Observable<StreamReadyEvent>;
    public passiveNotReady$: Observable<StreamNotReadyEvent>;

    protected socket: WebSocket;
    protected socketStateSubject$ = new BehaviorSubject<WebSocketConnectionState>(WebSocketConnectionState.CLOSED);
    protected socketState: WebSocketConnectionState = WebSocketConnectionState.CLOSED;

    protected socketMessageSubject$ = new Subject<WebSocketMessage>();
    protected socketErrorSubject$ = new Subject<CloseEvent>();

    protected socketHeartbeatSubject$ = new Subject<void>();
    protected isConnectionNotNeeded: boolean = true; // Tells that it was closed/not opened as unused.
    protected reconnectDelay: number = 0;
    protected isFirstConnectionTrying: boolean = true;
    protected reconnectInterval: number;

    protected openConnection$ = new Subject<void>();
    protected closeConnection$ = new Subject<void>();

    protected coreStreamState$: Observable<StreamEventType>;

    protected keepConnection$: Observable<any> = new Observable(() => {
        this.openConnection$.next();

        return () => this.closeConnection$.next();
    }).pipe(share());

    constructor(
        @Inject(STREAMING_SERVICE_CONFIG_TOKEN)
        @Optional()
        config: Partial<StreamingServiceConfig>,

        protected appConfig: AppConfigService,

        @Inject(SYSTEM_STATUS_LOGGER_TOKEN)
        @Optional()
        protected systemStatusLogger: GenericSystemStatusLogger
    ) {
        this.config = Object.assign(this.config, config);

        this.coreStreamState$ = this.socketStateSubject$.pipe(
            map((state: WebSocketConnectionState) =>
                state === WebSocketConnectionState.OPEN ? StreamEventType.READY : StreamEventType.NOT_READY
            ),
            distinctUntilChanged()
        );
        this.passiveReady$ = this.coreStreamState$.pipe(
            filter((type: StreamEventType): type is StreamEventType.READY => type === StreamEventType.READY),
            mapTo<StreamEventType, StreamReadyEvent>({type: StreamEventType.READY})
        );
        this.passiveNotReady$ = this.coreStreamState$.pipe(
            filter((type: StreamEventType): type is StreamEventType.NOT_READY => type === StreamEventType.NOT_READY),
            mapTo<StreamEventType, StreamNotReadyEvent>({type: StreamEventType.NOT_READY})
        );

        this.message$ = this.socketMessageSubject$.pipe(
            dependOn(this.keepConnection$),
            share()
        );
        this.ready$ = this.passiveReady$.pipe(
            dependOn(this.keepConnection$)
        );
        this.notReady$ = this.passiveNotReady$.pipe(
            dependOn(this.keepConnection$)
        );

        this.initConnectionSwitch();
        this.listenToHeartbeat();
    }

    public sendCommand(command: WebSocketCommand): void {
        console.log(`[${this.constructor.name}]`, 'Send Command', command);

        this.socket.send(JSON.stringify(command));
    }

    protected openConnection(): void {
        console.log(`[${this.constructor.name}]`, 'Open Connection');

        if (this.socketState !== WebSocketConnectionState.CLOSED) {
            console.error(
                `[${this.constructor.name}]`,
                'Trying to open connection while connection state is',
                this.socketState
            );

            return;
        }

        this.isConnectionNotNeeded = false;

        this.socketState = WebSocketConnectionState.OPENING;
        this.socketStateSubject$.next(this.socketState);

        this.createSocket();
    }

    protected createSocket(): void {

        // Fix wrong protocol in URL. Useful for switching from SockJS to native Websocket.
        const url = this.config.socketUrl
            .replace(/^http/, 'ws');

        this.socket = new WebSocket(url);

        this.socket.onopen = this.onSocketOpen.bind(this);
        this.socket.onmessage = this.onSocketMessage.bind(this);
        this.socket.onclose = this.onSocketClose.bind(this);
        this.socket.onerror = this.onSocketError.bind(this) as any;
    }

    protected closeConnection(
        {code, reason, doReconnect}: {code?: number, reason?: string, doReconnect?: boolean} = {}
    ): void {
        if (!code && !doReconnect) {
            code = NORMAL_DISCONNECT_CODE;
        }

        console.warn('CLOSE CONNECTION', code);

        this.isConnectionNotNeeded = !doReconnect;

        const isAlreadyClosedOrClosing = this.socketState === WebSocketConnectionState.CLOSED
            || this.socketState === WebSocketConnectionState.CLOSING;

        // When network is down, the state is CLOSED, if we change it to CLOSING, we'll never get it back to CLOSED.
        if (!isAlreadyClosedOrClosing) {
            this.socketState = WebSocketConnectionState.CLOSING;
            this.socketStateSubject$.next(this.socketState);

            this.socket.close(code, reason);
        }

        if (this.isConnectionNotNeeded) {
            this.logSystemStatus(
                true,
                `[${this.constructor.name}] Websocket connection not needed: ${JSON.stringify({
                    url: this.config.socketUrl
                })}`,
                [SystemStatusLogCode.NOT_NEEDED]
            );
        }
    }

    protected reconnect(): void {
        console.log(
            `[${this.constructor.name}]`,
            `Trying to reconnect in ${this.reconnectDelay / SECOND} seconds...`
        );

        clearTimeout(this.reconnectInterval);

        this.isFirstConnectionTrying = false;

        this.reconnectInterval = setTimeout(() => {
            this.openConnection();
            this.reconnectDelay = Math.min(
                this.reconnectDelay + this.config.reconnectDelayStep,
                this.config.maxReconnectDelay
            );
        }, this.reconnectDelay) as unknown as number; // TS may think, that it returns NodeJS.Timeout type.
    }

    protected onSocketOpen(): void {
        console.log(
            `[${this.constructor.name}]`,
            'Websocket connection opened'
        );

        this.reconnectDelay = 0;
        this.isFirstConnectionTrying = true;
        clearInterval(this.reconnectInterval);

        this.socketState = WebSocketConnectionState.OPEN;
        this.socketStateSubject$.next(this.socketState);

        this.logSystemStatus(
            true,
            `[${this.constructor.name}] Websocket connection opened: ${JSON.stringify({
                url: this.config.socketUrl
            })}`,
            [SystemStatusLogCode.OPEN]
        );
    }

    protected onSocketMessage(messageEvent: MessageEvent): void {
        this.markSocketServerIsAlive();

        if (!this.isControlSocketMessage(messageEvent)) {
            const message: WebSocketMessage = JSON.parse(messageEvent.data);

            this.socketMessageSubject$.next(message);
        }
    }

    protected isControlSocketMessage(messageEvent: MessageEvent): boolean {
        return messageEvent.data === HEARTBEAT_SOCKET_MESSAGE;
    }

    protected onSocketClose(closeEvent: CloseEvent): void {
        console.warn(
            `[${this.constructor.name}]`,
            'Websocket connection closed with code',
            closeEvent.code,
            closeEvent.reason
        );

        this.socketState = WebSocketConnectionState.CLOSED;
        this.socketStateSubject$.next(this.socketState);

        if (!this.isConnectionNotNeeded) {

            // Allow first reconnection to do silently
            if (!this.isFirstConnectionTrying) {
                this.socketErrorSubject$.next(closeEvent);

                this.logSystemStatus(
                    false,
                    `[${this.constructor.name}] Websocket connection closed: ${JSON.stringify({
                        url:        this.config.socketUrl,
                        closeEvent: {
                            code:   closeEvent.code,
                            reason: closeEvent.reason
                        }
                    })}`,
                    [SystemStatusLogCode.CLOSE, closeEvent.code]
                );
            }

            this.reconnect();
        }
    }

    protected onSocketError(event: ErrorEvent): void {
        console.warn(
            `[${this.constructor.name}]`,
            'Websocket connection error:',
            event.message
        );

        this.logSystemStatus(
            false,
            `[${this.constructor.name}] Websocket connection error: ${JSON.stringify({
                url:          this.config.socketUrl,
                eventMessage: event.message
            })}`,
            [SystemStatusLogCode.ERROR]
        );
    }

    protected markSocketServerIsAlive(): void {
        this.socketHeartbeatSubject$.next();
    }

    /**
     * Aborts socket connection if server doesn't respond for a long time.
     */
    protected listenToHeartbeat(): void {
        const HEARTBEAT_INTERVAL_OVERLAP = SECOND; // One more second to wait for late heartbeat

        const waitHeartbeat$ = this.socketHeartbeatSubject$.pipe(
            switchMapTo(timer(this.config.heartbeatInterval + HEARTBEAT_INTERVAL_OVERLAP))
        );
        const closeWithNoPulse$ = this.coreStreamState$.pipe(
            switchMap((state: StreamEventType) =>
                state === StreamEventType.READY
                    ? waitHeartbeat$
                    : NEVER
            )
        );

        closeWithNoPulse$
            .subscribe(() => this.closeConnection({
                code: NO_PULSE_DISCONNECT_CODE,
                reason: this.config.noPulseMessage,
                doReconnect: true
            }));
    }

    protected initConnectionSwitch(): void {
        const waitUntilClosed$ = this.socketStateSubject$.pipe(
            filter(state => state === WebSocketConnectionState.CLOSED),
            take(1)
        );

        const canOpenStreamMap: Record<WebSocketConnectionState, Observable<any>> = {
            [WebSocketConnectionState.OPEN]:    NEVER,
            [WebSocketConnectionState.OPENING]: NEVER,
            [WebSocketConnectionState.CLOSING]: waitUntilClosed$,
            [WebSocketConnectionState.CLOSED]:  of(null)
        };

        const openConnection = pipe(
            tap(() => this.cancelConnectionClosing()),
            switchMap(() => canOpenStreamMap[this.socketState]),
            tap(() => this.openConnection())
        );

        const closeConnection = pipe(
            delay(LAZY_DISCONNECT_TIMEOUT),
            tap(() => this.cancelConnectionOpening()),
            tap(() => this.closeConnection())
        );

        merge(
            this.openConnection$.pipe(mapTo(openConnection)),
            this.closeConnection$.pipe(mapTo(closeConnection))
        ).pipe(
            switchMap(operation => operation(of(null)))
        ).subscribe();
    }

    protected cancelConnectionOpening(): void {
        clearTimeout(this.reconnectInterval);
    }

    protected cancelConnectionClosing(): void {
        // Nothing to do.
    }

    protected logSystemStatus(isOk: boolean, message: string, codes: SystemStatusLogCode[]): void {
        if (this.systemStatusLogger) {
            this.systemStatusLogger.logStreamingStatus({
                key: this,
                isOk,
                message,
                codes: [
                    this.config.systemStatusLogKeyCode,
                    ...codes
                ]
            });
        }
    }

}
